import React, { ReactNode, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface Field {
  name: string;
  label: string;
  type: "text" | "color" | "select" | "component";
  children?: ReactNode;
  options?: Record<string, string>;
}

export interface Group {
  group: string;
  fields: Field[];
}

interface FieldByType {
  div: Group[];
  image: Group[];
  text: Group[];
}

const fieldsByType: FieldByType = {
  div: [
    {
      group: "Tamanho e Espaçamento", fields: [
        { name: "width", label: "Largura", type: "text" },
        { name: "height", label: "Altura", type: "text" },
        // { name: "margin", label: "Margem", type: "text" },
        // { name: "padding", label: "Padding", type: "text" },
      ]
    },
    {
      group: "Fundo", fields: [
        { name: "backgroundColor", label: "Cor de Fundo", type: "color" },
        // {
        //   name: "boxShadow", label: "Sombra", type: "select", options: {
        //     "Sem Sombra": "none",
        //     "Sombra 1": "0px 1px 3px rgba(0,0,0,0.2)",
        //     "Sombra 2": "0px 3px 6px rgba(0,0,0,0.3)",
        //     "Sombra 3": "0px 10px 20px rgba(0,0,0,0.4)",
        //   }
        // },
      ]
    },
    {
      group: "Texto", fields: [
        { name: "color", label: "Cor do Texto", type: 'color' },
        { name: "textAlign", label: "Alinhamento do Texto", type: "select", options: { "Esquerda": "left", "Centro": "center", "Direita": "right", "Justificado": "justify" } },
        {
          name: "overflow", label: "Scroll", type: "select", options: {
            "Cortar": "clip",
            "Automático": "auto",
            "Esconder": "hidden",
            "Visível": "visible",
          }
        },
      ]
    },
    {
      group: "Borda", fields: [
        { name: "borderWidth", label: "Largura da Borda", type: "text" },
        { name: "borderStyle", label: "Estilo da Borda", type: "select", options: { "____": "solid", "----": "dashed", "....": "dotted", "Dupla": "double", "Sem Borda": "none" } },
        { name: "borderColor", label: "Cor da Borda", type: "color" },
        { name: "borderRadius", label: "Arredondamento", type: "text" },
      ]
    },
  ],
  image: [
    {
      group: "Imagem", fields: [
        {
          name: "objectFit", label: "Ajuste", type: "select", options: {
            "Ajustar": "contain",
            "Cobrir Tudo": "cover",
            "Fill": "fill",
            "Automático": "none",
            "Escala a Baixo": "scale-down",
          }
        },
        { name: "borderRadius", label: "Arredondamento", type: "text" },
      ]
    }
  ],
  text: [
    {
      group: "Texto", fields: [
        { name: "color", label: "Cor do Texto", type: "color" },
        { name: "fontSize", label: "Tamanho da Fonte", type: "text" },
        { name: "fontWeight", label: "Peso da Fonte", type: "text" },
      ]
    }
  ]
};

type CssType = "div" | "image" | "text";

interface CssEditorProps {
  type: CssType;
  preGroups?: Group[];
  postGroups?: Group[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialPreStyle?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialPostStyle?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEditPreGroup?: (newStyle: Record<string, any>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEditPostGroup?: (newStyle: Record<string, any>) => void;
  initialStyle?: React.CSSProperties;
  onEdit?: (newStyle: React.CSSProperties) => void;
}

export const CssEditor: React.FC<CssEditorProps> = ({
  type,
  initialStyle,
  onEdit,
  preGroups,
  postGroups,
  initialPreStyle,
  initialPostStyle,
  onEditPreGroup,
  onEditPostGroup,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [preStyle, setPreStyle] = useState<Record<string, any>>(initialPreStyle ?? {});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [postStyle, setPostStyle] = useState<Record<string, any>>(initialPostStyle ?? {});
  const [style, setStyle] = useState<React.CSSProperties>(initialStyle ?? {});
  const timer = useRef<NodeJS.Timer>();

  const fieldGroups = useMemo(() => {
    const baseGroups = fieldsByType[type] ?? [];
    if (type === "div") {
      return baseGroups;
    } else {
      return [...fieldsByType["div"].slice(0, 2), ...baseGroups]; // Herda tamanho + fundo
    }
  }, [type]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (key: keyof React.CSSProperties, value: any) => {
    let tryValue = value;
    if (typeof value === "string") {
      tryValue = value.replaceAll(",", ".").replaceAll(" ", "");

      const justNumber = /^\d+$/.test(tryValue);
      if (justNumber) {
        tryValue = parseFloat(tryValue);
      } else {
        tryValue = value;
      }
    }

    if (isNaN(tryValue)) {
      tryValue = value;
    }

    const newStyle = {
      ...style,
      [key]: tryValue,
    };
    setStyle(newStyle);

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      onEdit?.(newStyle);
    }, 300);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeExtra = (position: "pre" | "post", key: keyof React.CSSProperties, value: any) => {
    let tryValue = value;
    if (typeof value === "string") {
      tryValue = value.replaceAll(",", ".").replaceAll(" ", "");

      const justNumber = /^\d+$/.test(tryValue);
      if (justNumber) {
        tryValue = parseFloat(tryValue);
      } else {
        tryValue = value;
      }
    }

    if (isNaN(tryValue)) {
      tryValue = value;
    }

    let newStyle = {};
    if (position === 'pre') {
      newStyle = {
        ...preStyle,
        [key]: tryValue,
      };
      setPreStyle(newStyle);
    } else if (position === 'post') {
      newStyle = {
        ...postStyle,
        [key]: tryValue,
      };
      setPostStyle(newStyle);
    }

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (position === 'pre') onEditPreGroup?.(newStyle);
      else if (position === 'post') onEditPostGroup?.(newStyle);
    }, 300);
  };

  return (
    <Box>
      <Typography variant="h5" mb={1}>Estilo</Typography>
      <Divider sx={{ mb: 1 }} />

      {preGroups &&
        preGroups.map((group) => (
          <Accordion key={group.group} disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold" fontSize="0.95rem">{group.group}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={1}>
                {group.fields.map((field) => (
                  field.type === 'component' ?
                    (field.children ?? <div key={field.name} />) :
                    <TextField
                      key={field.name}
                      fullWidth
                      size="small"
                      margin="dense"
                      type={field.type}
                      label={field.label}
                      select={field.type === "select"}
                      value={style[field.name as keyof React.CSSProperties] ?? ""}
                      onChange={(e) => handleChangeExtra("pre", field.name as keyof React.CSSProperties, e.target.value)}
                    >
                      {field.type === "select" && field.options && Object.keys(field.options).map((key) => (
                        <MenuItem key={key} value={field.options?.[key] ?? ""}>
                          {key}
                        </MenuItem>
                      ))}
                    </TextField>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      }

      {fieldGroups.map((group) => (
        <Accordion key={group.group} disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold" fontSize="0.95rem">{group.group}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={1}>
              {group.fields.map((field) => (
                field.type === 'component' ?
                  (field.children ?? <div key={field.name} />) :
                  <TextField
                    key={field.name}
                    fullWidth
                    size="small"
                    margin="dense"
                    type={field.type}
                    label={field.label}
                    select={field.type === "select"}
                    value={style[field.name as keyof React.CSSProperties] ?? ""}
                    onChange={(e) => handleChange(field.name as keyof React.CSSProperties, e.target.value)}
                  >
                    {field.type === "select" && field.options && Object.keys(field.options).map((key) => (
                      <MenuItem key={key} value={field.options?.[key] ?? ""}>
                        {key}
                      </MenuItem>
                    ))}
                  </TextField>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {postGroups &&
        postGroups.map((group) => (
          <Accordion key={group.group} disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold" fontSize="0.95rem">{group.group}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={1}>
                {group.fields.map((field) => (
                  field.type === 'component' ?
                    (field.children ?? <div key={field.name} />) :
                    <TextField
                      key={field.name}
                      fullWidth
                      size="small"
                      margin="dense"
                      type={field.type}
                      label={field.label}
                      select={field.type === "select"}
                      value={style[field.name as keyof React.CSSProperties] ?? ""}
                      onChange={(e) => handleChangeExtra("post", field.name as keyof React.CSSProperties, e.target.value)}
                    >
                      {field.type === "select" && field.options && Object.keys(field.options).map((key) => (
                        <MenuItem key={key} value={field.options?.[key] ?? ""}>
                          {key}
                        </MenuItem>
                      ))}
                    </TextField>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      }
    </Box>
  );
};
